import { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import './ui.css';

/**
 * Componentes basicos da interface — Glassmorphism Edition
 */

export function Botao({ variante = 'primario', tamanho = 'md', carregando, filhos, children, className, ...resto }) {
  const conteudo = children ?? filhos;
  return (
    <button
      // `className` SOMA ao visual do botao (antes substituia e o botao perdia o estilo).
      className={juntar('botao', `botao--${variante}`, `botao--${tamanho}`, className)}
      disabled={carregando || resto.disabled}
      {...resto}
    >
      {carregando && <span className="botao__girando" aria-hidden="true" />}
      {conteudo}
    </button>
  );
}

export function Campo({ rotulo, erro, dica, children, obrigatorio }) {
  return (
    <label className="campo">
      <span className="campo__rotulo">
        {rotulo}
        {obrigatorio && <span aria-hidden="true" className="campo__obrigatorio"> *</span>}
      </span>
      {children}
      {erro && <span className="campo__erro" role="alert">{erro}</span>}
      {!erro && dica && <span className="campo__dica">{dica}</span>}
    </label>
  );
}

const juntar = (...classes) => classes.filter(Boolean).join(' ');

export function Entrada({ erro, className, ...resto }) {
  // Data usa o calendario do sistema (o nativo muda de cara em cada navegador).
  if (resto.type === 'date') return <CampoData erro={erro} className={className} {...resto} />;
  return <input className={juntar('entrada', erro && 'entrada--erro', className)} aria-invalid={Boolean(erro)} {...resto} />;
}

/* ─── CampoData: calendario do sistema ───────────────────────────────────── */

const DIAS_SEMANA = ['SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB', 'DOM'];
const NOMES_MES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const MESES_CURTOS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

// Contas em UTC sobre 'AAAA-MM-DD': o fuso do navegador nunca muda o dia.
const isoDe = (a, m, d) => new Date(Date.UTC(a, m, d)).toISOString().slice(0, 10);
const partesDe = (iso) => {
  const [a, m, d] = iso.split('-').map(Number);
  return { a, m: m - 1, d };
};
const somarDiasIso = (iso, n) => {
  const { a, m, d } = partesDe(iso);
  return isoDe(a, m, d + n);
};
const hojeIso = () => new Date().toLocaleDateString('sv-SE');
const isoValido = (v) => /^\d{4}-\d{2}-\d{2}$/.test(v ?? '');

/**
 * Campo de data com o calendario do sistema (cartao com "Mes Ano", setas, dias
 * da semana a partir de segunda e o dia escolhido num circulo). Mesma API do
 * <input type="date">: value 'AAAA-MM-DD', onChange({ target: { value } }),
 * min, max, disabled, required. Clicar no titulo troca para meses e anos
 * (data de nascimento sem clicar 40 vezes na seta).
 */
export function CampoData({ erro, className, value, onChange, min, max, disabled, required, name, id, style, type: _tipo, placeholder, ...resto }) {
  const [aberto, setAberto] = useState(false);
  const [vista, setVista] = useState('dias'); // dias | meses | anos
  const [mesVisto, setMesVisto] = useState(() => partesDe(isoValido(value) ? value : hojeIso()));
  const [foco, setFoco] = useState(isoValido(value) ? value : hojeIso());
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const gatilhoRef = useRef(null);
  const painelRef = useRef(null);

  const fora = (iso) => (min && iso < min) || (max && iso > max);

  /**
   * Sempre DENTRO da janela: abre embaixo do campo; sem espaco embaixo (campo
   * no rodape de um modal), abre em cima; sem espaco nem em cima, encosta na
   * borda que couber. A altura vem do painel ja desenhado (meses e dias tem
   * alturas diferentes); antes de medir, usa uma estimativa.
   */
  const calcularPos = useCallback(() => {
    const r = gatilhoRef.current?.getBoundingClientRect();
    if (!r) return;
    const largura = 304;
    const altura = painelRef.current?.offsetHeight || 400;
    const margem = 8;
    const left = Math.max(margem, Math.min(r.left, window.innerWidth - largura - margem));
    let top = r.bottom + 6;
    if (top + altura > window.innerHeight - margem) {
      const emCima = r.top - 6 - altura;
      top = emCima >= margem ? emCima : Math.max(margem, window.innerHeight - altura - margem);
    }
    setPos({ top, left });
  }, []);

  const abrir = () => {
    if (disabled) return;
    const base = isoValido(value) ? value : hojeIso();
    setMesVisto(partesDe(base));
    setFoco(base);
    setVista('dias');
    calcularPos();
    setAberto(true);
  };
  const fechar = (devolverFoco = true) => {
    setAberto(false);
    if (devolverFoco) gatilhoRef.current?.focus();
  };
  const escolher = (iso) => {
    if (fora(iso)) return;
    onChange?.({ target: { value: iso, name: name ?? '' } });
    fechar();
  };

  useEffect(() => {
    if (!aberto) return;
    const clique = (e) => {
      if (!gatilhoRef.current?.contains(e.target) && !painelRef.current?.contains(e.target)) setAberto(false);
    };
    const reposicionar = () => calcularPos();
    document.addEventListener('mousedown', clique);
    window.addEventListener('scroll', reposicionar, true);
    window.addEventListener('resize', reposicionar);
    return () => {
      document.removeEventListener('mousedown', clique);
      window.removeEventListener('scroll', reposicionar, true);
      window.removeEventListener('resize', reposicionar);
    };
  }, [aberto, calcularPos]);

  // Depois de desenhado (e ao trocar dias/meses/anos), mede a altura real e reposiciona.
  useLayoutEffect(() => {
    if (aberto) calcularPos();
  }, [aberto, vista, mesVisto.a, mesVisto.m, calcularPos]);

  // O dia em foco recebe o foco do teclado.
  useEffect(() => {
    if (aberto && vista === 'dias') painelRef.current?.querySelector(`[data-dia="${foco}"]`)?.focus();
  }, [aberto, vista, foco]);

  const moverFoco = (iso) => {
    setFoco(iso);
    const p = partesDe(iso);
    if (p.a !== mesVisto.a || p.m !== mesVisto.m) setMesVisto({ a: p.a, m: p.m, d: 1 });
  };
  const teclaNoDia = (e) => {
    const passos = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
    if (passos[e.key]) {
      e.preventDefault();
      moverFoco(somarDiasIso(foco, passos[e.key]));
    } else if (e.key === 'PageUp' || e.key === 'PageDown') {
      e.preventDefault();
      const p = partesDe(foco);
      moverFoco(isoDe(p.a, p.m + (e.key === 'PageUp' ? -1 : 1), Math.min(p.d, 28)));
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      escolher(foco);
    }
  };
  const teclaNoPainel = (e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      fechar();
    }
  };
  const mudarMes = (n) => {
    const p = { ...mesVisto };
    const d = new Date(Date.UTC(p.a, p.m + n, 1));
    setMesVisto({ a: d.getUTCFullYear(), m: d.getUTCMonth(), d: 1 });
  };

  // Grade do mes: comeca na segunda; antes do dia 1 fica em branco.
  const primeiro = new Date(Date.UTC(mesVisto.a, mesVisto.m, 1));
  const vazios = (primeiro.getUTCDay() + 6) % 7;
  const totalDias = new Date(Date.UTC(mesVisto.a, mesVisto.m + 1, 0)).getUTCDate();
  const hoje = hojeIso();
  const inicioDecada = Math.floor(mesVisto.a / 12) * 12;

  const titulo =
    vista === 'dias' ? `${NOMES_MES[mesVisto.m]} ${mesVisto.a}` : vista === 'meses' ? String(mesVisto.a) : `${inicioDecada} – ${inicioDecada + 11}`;
  const voltar = () => (vista === 'dias' ? mudarMes(-1) : vista === 'meses' ? setMesVisto({ ...mesVisto, a: mesVisto.a - 1 }) : setMesVisto({ ...mesVisto, a: mesVisto.a - 12 }));
  const avancar = () => (vista === 'dias' ? mudarMes(1) : vista === 'meses' ? setMesVisto({ ...mesVisto, a: mesVisto.a + 1 }) : setMesVisto({ ...mesVisto, a: mesVisto.a + 12 }));

  const chevron = (d) => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={d} />
    </svg>
  );

  const painel = aberto
    ? createPortal(
        <div
          ref={painelRef}
          className="cd__painel"
          role="dialog"
          aria-label="Escolher data"
          // Fixo na janela (as contas acima sao do viewport): rolar a pagina reposiciona.
          style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999 }}
          onKeyDown={teclaNoPainel}
        >
          <div className="cd__topo">
            <button
              type="button"
              className="cd__titulo"
              onClick={() => setVista(vista === 'dias' ? 'meses' : vista === 'meses' ? 'anos' : 'dias')}
              title={vista === 'dias' ? 'Escolher mês e ano' : 'Voltar aos dias'}
            >
              {titulo}
            </button>
            <div className="cd__setas">
              <button type="button" className="cd__seta" onClick={voltar} aria-label="Anterior">
                {chevron('M15 18l-6-6 6-6')}
              </button>
              <button type="button" className="cd__seta" onClick={avancar} aria-label="Próximo">
                {chevron('M9 18l6-6-6-6')}
              </button>
            </div>
          </div>

          {vista === 'dias' && (
            <>
              <div className="cd__semana" aria-hidden="true">
                {DIAS_SEMANA.map((d) => (
                  <span key={d}>{d}</span>
                ))}
              </div>
              <div className="cd__grade" role="grid">
                {Array.from({ length: vazios }, (_, i) => (
                  <span key={`v${i}`} />
                ))}
                {Array.from({ length: totalDias }, (_, i) => {
                  const iso = isoDe(mesVisto.a, mesVisto.m, i + 1);
                  const escolhido = iso === value;
                  return (
                    <button
                      key={iso}
                      type="button"
                      data-dia={iso}
                      tabIndex={iso === foco ? 0 : -1}
                      disabled={fora(iso)}
                      aria-pressed={escolhido}
                      aria-label={new Date(`${iso}T12:00:00`).toLocaleDateString('pt-BR', { dateStyle: 'full' })}
                      className={juntar('cd__dia', escolhido && 'cd__dia--escolhido', iso === hoje && 'cd__dia--hoje')}
                      onClick={() => escolher(iso)}
                      onKeyDown={teclaNoDia}
                    >
                      {i + 1}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {vista === 'meses' && (
            <div className="cd__grade cd__grade--larga">
              {MESES_CURTOS.map((nome, m) => (
                <button
                  key={nome}
                  type="button"
                  className={juntar('cd__celula', isoValido(value) && partesDe(value).a === mesVisto.a && partesDe(value).m === m && 'cd__dia--escolhido')}
                  onClick={() => {
                    setMesVisto({ a: mesVisto.a, m, d: 1 });
                    setVista('dias');
                  }}
                >
                  {nome}
                </button>
              ))}
            </div>
          )}

          {vista === 'anos' && (
            <div className="cd__grade cd__grade--larga">
              {Array.from({ length: 12 }, (_, i) => inicioDecada + i).map((a) => (
                <button
                  key={a}
                  type="button"
                  className={juntar('cd__celula', isoValido(value) && partesDe(value).a === a && 'cd__dia--escolhido')}
                  onClick={() => {
                    setMesVisto({ ...mesVisto, a });
                    setVista('meses');
                  }}
                >
                  {a}
                </button>
              ))}
            </div>
          )}

          <div className="cd__rodape">
            {!required && value ? (
              <button
                type="button"
                className="cd__link"
                onClick={() => {
                  onChange?.({ target: { value: '', name: name ?? '' } });
                  fechar();
                }}
              >
                Limpar
              </button>
            ) : (
              <span />
            )}
            <button type="button" className="cd__link" disabled={fora(hoje)} onClick={() => escolher(hoje)}>
              Hoje
            </button>
          </div>
        </div>,
        document.body
      )
    : null;

  const texto = isoValido(value) ? value.split('-').reverse().join('/') : '';
  return (
    <>
      <button
        ref={gatilhoRef}
        type="button"
        id={id}
        name={name}
        style={style}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={aberto}
        aria-invalid={Boolean(erro)}
        className={juntar('entrada', 'cd', erro && 'entrada--erro', className)}
        onClick={() => (aberto ? fechar(false) : abrir())}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            abrir();
          }
        }}
        {...resto}
      >
        <span className={juntar('cd__valor', !texto && 'cd__valor--vazio')}>{texto || placeholder || 'dd/mm/aaaa'}</span>
        <svg className="cd__icone" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="5" width="18" height="16" rx="3" />
          <path d="M3 10h18M8 3v4M16 3v4" />
        </svg>
      </button>
      {painel}
    </>
  );
}

export function AreaTexto({ erro, className, ...resto }) {
  return <textarea className={juntar('entrada', 'entrada--area', erro && 'entrada--erro', className)} aria-invalid={Boolean(erro)} {...resto} />;
}

/**
 * Selecao — Select customizado com glassmorphism completo.
 *
 * Usa createPortal para renderizar o painel no <body>, evitando
 * qualquer corte por overflow:hidden em containers pais.
 * API identica ao <select> nativo: value, onChange(e), <option> children.
 */
export function Selecao({ erro, className, children, value, onChange, disabled, id, name, ...resto }) {
  const [aberto, setAberto] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0, abrirCima: false });
  const gatilhoRef = useRef(null);
  const listRef = useRef(null);

  /**
   * Extrai as opcoes dos <option>/<optgroup> filhos.
   *
   * Precisa ser RECURSIVO. O JSX quase nunca entrega uma lista plana: escrever
   *
   *   <Selecao>
   *     <option value="">Escolha</option>
   *     {lista.map((c) => <option key={c.id}>{c.nome}</option>)}
   *   </Selecao>
   *
   * produz `[<option>, [<option>, <option>, ...]]` — o `.map` vira UM filho que
   * e um array. Uma varredura de um nivel so ve esse array, nao reconhece nele
   * um `option`, e descarta a lista inteira: o campo abre mostrando apenas o
   * placeholder, como se a busca nao tivesse trazido nada. Era isso que
   * impedia escolher cliente e profissional na tela de marcar horario.
   *
   * Fragmentos (<>...</>) e condicionais entram pelo mesmo caminho.
   */
  const opcoes = [];

  /**
   * Texto de um <option>.
   *
   * `{c.nome} — {c.preco}` chega como ARRAY de pedacos. `String(array)` junta
   * com virgulas e produz "Ana, — ,R$ 45,00"; por isso concatenamos na mao.
   */
  const textoDe = (filhos) => {
    if (filhos === null || filhos === undefined || typeof filhos === 'boolean') return '';
    if (Array.isArray(filhos)) return filhos.map(textoDe).join('');
    if (typeof filhos === 'object') return textoDe(filhos.props?.children);
    return String(filhos);
  };

  const extrairOpcoes = (node) => {
    if (node === null || node === undefined || typeof node === 'boolean') return;

    if (Array.isArray(node)) {
      node.forEach(extrairOpcoes);
      return;
    }
    if (typeof node !== 'object') return;

    if (node.type === 'option') {
      opcoes.push({
        value: String(node.props?.value ?? ''),
        label: textoDe(node.props?.children),
        group: null,
        disabled: !!node.props?.disabled
      });
      return;
    }

    if (node.type === 'optgroup') {
      const label = node.props?.label ?? '';
      const antes = opcoes.length;
      extrairOpcoes(node.props?.children);
      // Marca como do grupo so o que acabou de entrar.
      for (let i = antes; i < opcoes.length; i++) opcoes[i].group = label;
      return;
    }

    // Fragmento ou qualquer outro embrulho: desce nos filhos.
    if (node.props?.children) extrairOpcoes(node.props.children);
  };
  extrairOpcoes(children);

  const selecionada = opcoes.find((o) => String(o.value) === String(value ?? ''));
  const rotulo = selecionada?.label ?? (opcoes[0]?.label ?? 'Selecione...');

  // Calcula posicao do painel relativa ao viewport (portal no body)
  const calcularPos = useCallback(() => {
    if (!gatilhoRef.current) return;
    const rect = gatilhoRef.current.getBoundingClientRect();
    const alturaEstimadaPainel = Math.min(opcoes.length * 38 + 20, 260);
    // Sempre para BAIXO: menu que abre para cima confunde e some atras da barra
    // do navegador. Se faltar espaco embaixo, a lista rola por dentro e a pagina
    // pode rolar junto (o painel e absoluto no documento).
    const abrirCima = false;

    setPos({
      top: abrirCima ? rect.top + window.scrollY - alturaEstimadaPainel - 4 : rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
      width: rect.width,
      abrirCima,
    });
  }, [opcoes.length]);

  const abrirDropdown = useCallback(() => {
    if (disabled) return;
    calcularPos();
    setAberto(true);
  }, [disabled, calcularPos]);

  // Fecha ao clicar fora
  useEffect(() => {
    if (!aberto) return;
    const handler = (e) => {
      const clickNoGatilho = gatilhoRef.current?.contains(e.target);
      const clickNoPainel = listRef.current?.contains(e.target);
      if (!clickNoGatilho && !clickNoPainel) setAberto(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [aberto]);

  // Recalcula posicao ao rolar ou redimensionar
  useEffect(() => {
    if (!aberto) return;
    const handler = () => calcularPos();
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    };
  }, [aberto, calcularPos]);

  // Foco no item ativo (ou primeiro) ao abrir
  useEffect(() => {
    if (!aberto || !listRef.current) return;
    const ativo = listRef.current.querySelector('.sg__item--ativo');
    const primeiro = listRef.current.querySelector('.sg__item:not(.sg__item--disabled)');
    (ativo ?? primeiro)?.focus();
  }, [aberto]);

  const selecionar = useCallback((val) => {
    if (onChange) onChange({ target: { value: val, name: name ?? '' } });
    setAberto(false);
    gatilhoRef.current?.focus();
  }, [onChange, name]);

  // Teclado no gatilho
  const onKeyDown = (e) => {
    if (disabled) return;
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); aberto ? setAberto(false) : abrirDropdown(); }
    if (e.key === 'Escape') setAberto(false);
    if (e.key === 'ArrowDown') { e.preventDefault(); aberto ? listRef.current?.querySelector('.sg__item:not(.sg__item--disabled)')?.focus() : abrirDropdown(); }
    if (e.key === 'ArrowUp') { e.preventDefault(); abrirDropdown(); }
  };

  // Teclado nos itens
  const onItemKeyDown = (e, val) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selecionar(val); }
    if (e.key === 'Escape') { setAberto(false); gatilhoRef.current?.focus(); }
    if (e.key === 'ArrowDown') { e.preventDefault(); const next = e.currentTarget.nextElementSibling; if (next) { next.focus(); } }
    if (e.key === 'ArrowUp') { e.preventDefault(); const prev = e.currentTarget.previousElementSibling; if (prev) { prev.focus(); } else { gatilhoRef.current?.focus(); } }
  };

  // Agrupar
  const grupos = [];
  const vistos = new Set();
  opcoes.forEach((o) => { if (o.group && !vistos.has(o.group)) { vistos.add(o.group); grupos.push(o.group); } });
  const temGrupos = grupos.length > 0;

  const renderOpcao = (o, i) => {
    const ativo = String(o.value) === String(value ?? '');
    return (
      <div
        key={`${o.value}-${i}`}
        role="option"
        aria-selected={ativo}
        aria-disabled={o.disabled}
        tabIndex={o.disabled ? -1 : 0}
        className={juntar('sg__item', ativo && 'sg__item--ativo', o.disabled && 'sg__item--disabled')}
        onClick={() => !o.disabled && selecionar(o.value)}
        onKeyDown={(e) => !o.disabled && onItemKeyDown(e, o.value)}
      >
        {ativo && (
          <svg className="sg__check" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M2.5 8l4 4 7-7" />
          </svg>
        )}
        <span>{o.label}</span>
      </div>
    );
  };

  const painel = aberto ? createPortal(
    <div
      ref={listRef}
      role="listbox"
      aria-label="Selecione uma opcao"
      className={juntar('sg__painel', pos.abrirCima && 'sg__painel--cima')}
      style={{
        position: 'absolute',
        top: pos.top,
        left: pos.left,
        width: pos.width,
        zIndex: 9999,
      }}
    >
      <div className="sg__lista">
        {temGrupos ? (
          <>
            {opcoes.filter(o => !o.group).map((o, i) => renderOpcao(o, i))}
            {grupos.map((grupo) => (
              <div key={grupo} className="sg__grupo">
                <div className="sg__grupo-label">{grupo}</div>
                {opcoes.filter((o) => o.group === grupo).map((o, i) => renderOpcao(o, i))}
              </div>
            ))}
          </>
        ) : (
          opcoes.map((o, i) => renderOpcao(o, i))
        )}
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <div
      className={juntar('sg', erro && 'sg--erro', disabled && 'sg--disabled', aberto && 'sg--aberto', className)}
      {...resto}
    >
      <button
        ref={gatilhoRef}
        type="button"
        role="combobox"
        id={id}
        aria-expanded={aberto}
        aria-haspopup="listbox"
        aria-disabled={disabled}
        disabled={disabled}
        className="sg__gatilho"
        onClick={() => aberto ? setAberto(false) : abrirDropdown()}
        onKeyDown={onKeyDown}
      >
        <span className="sg__valor">{rotulo}</span>
        <svg
          className={juntar('sg__seta', aberto && 'sg__seta--aberta')}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {painel}
    </div>
  );
}

export function Cartao({ titulo, acao, children, semPadding }) {
  return (
    <section className="cartao">
      {(titulo || acao) && (
        <header className="cartao__topo">
          {titulo && <h2 className="cartao__titulo">{titulo}</h2>}
          {acao}
        </header>
      )}
      <div className={semPadding ? '' : 'cartao__corpo'}>{children}</div>
    </section>
  );
}

/**
 * Foto do cliente — a mesma em toda tela onde ele aparece.
 *
 * Sem foto, mostra a inicial do nome: um circulo vazio faria a lista parecer
 * quebrada. A foto vem do perfil do WhatsApp (ver `leads.sincronizarFoto` na
 * API), entao boa parte dos clientes tem uma sem ninguem ter feito nada.
 */
export function FotoLead({ nome, url, tamanho = 36 }) {
  const inicial = (nome ?? '?').trim().slice(0, 1).toUpperCase();
  return (
    <span className="avatar" style={{ width: tamanho, height: tamanho, fontSize: Math.round(tamanho * 0.4) }}>
      {url ? <img src={url} alt="" /> : <span aria-hidden="true">{inicial}</span>}
    </span>
  );
}

export function Etiqueta({ tom = 'neutro', children }) {
  return <span className={`etiqueta etiqueta--${tom}`}>{children}</span>;
}

export const TOM_STATUS = {
  pre_agendado: 'alerta', pendente: 'alerta', confirmado: 'info', em_andamento: 'primario', concluido: 'sucesso',
  cancelado: 'neutro', faltou: 'perigo', bot: 'info', na_fila: 'alerta', humana: 'primario',
  finalizada: 'neutro', rascunho: 'neutro', gerando: 'info', revisao: 'alerta', pronta: 'info', enviando: 'primario', pausada: 'alerta',
  concluida: 'sucesso', cancelada: 'neutro', conectado: 'sucesso', conectando: 'alerta',
  aguardando_qr: 'alerta', desconectado: 'neutro', erro: 'perigo'
};

export const ROTULO_STATUS = {
  pre_agendado: 'Pré-agendado (aguarda confirmação)', pendente: 'Pendente', confirmado: 'Confirmado', em_andamento: 'Em andamento', concluido: 'Concluido',
  cancelado: 'Cancelado', faltou: 'Faltou', bot: 'Com a IA', na_fila: 'Na fila', humana: 'Com atendente',
  finalizada: 'Finalizada', rascunho: 'Rascunho', gerando: 'Gerando mensagens', revisao: 'Em revisão', pronta: 'Pronta',
  enviando: 'Enviando', pausada: 'Pausada', concluida: 'Concluída', cancelada: 'Parada', conectado: 'Conectado', conectando: 'Conectando',
  aguardando_qr: 'Aguardando QR', desconectado: 'Desconectado', erro: 'Com erro'
};

/** `rotulo`: troca o texto padrao mantendo a cor do status (ex.: "Com Camila"). */
export function Status({ valor, rotulo }) {
  return <Etiqueta tom={TOM_STATUS[valor] ?? 'neutro'}>{rotulo ?? ROTULO_STATUS[valor] ?? valor}</Etiqueta>;
}

export function Vazio({ titulo, descricao, acao }) {
  return (
    <div className="vazio">
      <p className="vazio__titulo">{titulo}</p>
      {descricao && <p className="vazio__descricao">{descricao}</p>}
      {acao && <div className="vazio__acao">{acao}</div>}
    </div>
  );
}

export function Carregando({ texto = 'Carregando...' }) {
  return (
    <div className="carregando" role="status">
      <span className="carregando__girando" aria-hidden="true" />
      <span>{texto}</span>
    </div>
  );
}

const AVISO_ICONES = {
  info:    '●',
  sucesso: '✓',
  alerta:  '▲',
  perigo:  '⊗',
};

export function Aviso({ tom = 'info', titulo, children, aoFechar }) {
  return (
    <div className={`aviso aviso--${tom}`} role={tom === 'perigo' ? 'alert' : 'status'}>
      <span className="aviso__icone" aria-hidden="true">{AVISO_ICONES[tom] ?? '●'}</span>
      <div className="aviso__corpo">
        {titulo && <strong className="aviso__titulo">{titulo}</strong>}
        <div className="aviso__texto">{children}</div>
      </div>
      {aoFechar && (
        <button className="aviso__fechar" onClick={aoFechar} aria-label="Fechar aviso">×</button>
      )}
    </div>
  );
}

export function Modal({ titulo, aberto, aoFechar, children, rodape, largura = 520, className }) {
  if (!aberto) return null;
  // No <body>, fora de qualquer cartao: um ancestral com backdrop-filter ou
  // transform vira o "bloco de contencao" do position:fixed, e o modal abria
  // espremido dentro do cartao que o chamou (a caixa da campanha).
  return createPortal(
    <div className="modal__fundo" onClick={aoFechar} role="presentation">
      <div
        className={juntar('modal', className)}
        style={{ maxWidth: largura }}
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="modal__topo">
          <h2>{titulo}</h2>
          <button className="modal__fechar" onClick={aoFechar} aria-label="Fechar">×</button>
        </header>
        <div className="modal__corpo">{children}</div>
        {rodape && <footer className="modal__rodape">{rodape}</footer>}
      </div>
    </div>,
    document.body
  );
}

export function Tabela({ cabecalho, children, className }) {
  // No celular a tabela vira lista de cartoes e cada celula mostra o nome da
  // coluna ao lado do valor (ver estilos/celular.css). O nome vai por variavel
  // CSS: funciona ate com linhas que sao componentes proprios, sem precisar
  // que cada tela repita o rotulo em todo <td>. Cabecalho que nao e texto
  // (um checkbox de "marcar todos") fica sem rotulo.
  const rotulos = Object.fromEntries(
    cabecalho.map((c, i) => [`--rotulo-${i + 1}`, JSON.stringify(typeof c === 'string' ? c : '')])
  );
  return (
    <div className="tabela__rolagem">
      <table className={juntar('tabela', className)} style={rotulos}>
        <thead>
          <tr>
            {cabecalho.map((c) => (<th key={c} scope="col">{c}</th>))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function Metrica({ rotulo, valor, detalhe, tom }) {
  return (
    <div className="metrica">
      <span className="metrica__rotulo">{rotulo}</span>
      <strong className={`metrica__valor ${tom ? `metrica__valor--${tom}` : ''}`}>{valor}</strong>
      {detalhe && <span className="metrica__detalhe">{detalhe}</span>}
    </div>
  );
}

/**
 * PAGINACAO: toda lista do sistema mostra no maximo 30 itens; acima disso,
 * paginas. Uma regra so para a casa inteira (decisao do dono) — lista longa
 * sem fim trava o celular e esconde o que importa.
 */
export const ITENS_POR_PAGINA = 30;

/**
 * Pagina uma lista que ja esta na tela (filtrada no navegador).
 * Volta para a pagina 1 quando a lista muda de tamanho (filtro, busca) e
 * nunca fica numa pagina que deixou de existir.
 *
 * @returns {{ itens: any[], pagina: number, paginas: number, total: number, irPara: (n: number) => void }}
 */
export function usePaginacao(lista, porPagina = ITENS_POR_PAGINA) {
  const total = lista?.length ?? 0;
  const paginas = Math.max(1, Math.ceil(total / porPagina));
  const [pagina, setPagina] = useState(1);
  useEffect(() => {
    setPagina(1);
  }, [total]);
  const atual = Math.min(pagina, paginas);
  return {
    itens: (lista ?? []).slice((atual - 1) * porPagina, atual * porPagina),
    pagina: atual,
    paginas,
    total,
    irPara: (n) => setPagina(Math.min(Math.max(1, n), paginas))
  };
}

/** Controles de pagina. Some sozinho quando tudo cabe numa pagina. */
export function Paginacao({ pagina, paginas, total, irPara, porPagina = ITENS_POR_PAGINA, rotulo = 'itens' }) {
  if (!paginas || paginas <= 1) return null;
  const de = (pagina - 1) * porPagina + 1;
  const ate = Math.min(pagina * porPagina, total);
  return (
    <nav className="paginacao" aria-label="Paginação">
      <span className="paginacao__info">
        {de}–{ate} de {total} {rotulo}
      </span>
      <div className="paginacao__botoes">
        <Botao type="button" variante="secundario" tamanho="sm" disabled={pagina <= 1} onClick={() => irPara(1)} aria-label="Primeira página">
          «
        </Botao>
        <Botao type="button" variante="secundario" tamanho="sm" disabled={pagina <= 1} onClick={() => irPara(pagina - 1)}>
          Anterior
        </Botao>
        <span className="paginacao__pagina" aria-current="page">
          {pagina} / {paginas}
        </span>
        <Botao type="button" variante="secundario" tamanho="sm" disabled={pagina >= paginas} onClick={() => irPara(pagina + 1)}>
          Próxima
        </Botao>
        <Botao type="button" variante="secundario" tamanho="sm" disabled={pagina >= paginas} onClick={() => irPara(paginas)} aria-label="Última página">
          »
        </Botao>
      </div>
    </nav>
  );
}

/**
 * Paginacao por CURSOR (o servidor diz so "tem mais": listas grandes, como os
 * contatos, que nao contam o total a cada pagina). Guarda os cursores ja
 * vistos para o "Anterior" voltar sem nova regra no servidor.
 */
export function usePaginacaoPorCursor(chaveDoFiltro) {
  const [cursores, setCursores] = useState([undefined]);
  useEffect(() => {
    setCursores([undefined]);
  }, [chaveDoFiltro]);
  const pagina = cursores.length;
  return {
    cursor: cursores[cursores.length - 1],
    pagina,
    proxima: (proximoCursor) => proximoCursor && setCursores((c) => [...c, proximoCursor]),
    anterior: () => setCursores((c) => (c.length > 1 ? c.slice(0, -1) : c))
  };
}

export function PaginacaoPorCursor({ pagina, temProxima, aoProxima, aoAnterior, quantidade, rotulo = 'itens' }) {
  if (pagina <= 1 && !temProxima) return null;
  return (
    <nav className="paginacao" aria-label="Paginação">
      <span className="paginacao__info">
        Página {pagina}
        {quantidade != null ? ` · ${quantidade} ${rotulo}` : ''}
      </span>
      <div className="paginacao__botoes">
        <Botao type="button" variante="secundario" tamanho="sm" disabled={pagina <= 1} onClick={aoAnterior}>
          Anterior
        </Botao>
        <Botao type="button" variante="secundario" tamanho="sm" disabled={!temProxima} onClick={aoProxima}>
          Próxima
        </Botao>
      </div>
    </nav>
  );
}
